import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EDEN_ENTRY_ATTACHMENT_POLICY,
  dispatchEdenEntryPrompt,
  setEdenEntryDismissedForSession,
} from "../config/edenEntry";
import profileSettingsImage from "../assets/profilesettings.jpg";
import "../pages/Dashboard.css";
import "./EdenPromptPerimeter.css";
import "../styles/CpherePrimaryAction.css";

// Each quick action keeps its accent icon, primary label and navigation chevron.
export const EDEN_ENTRY_SUGGESTIONS = [
  {
    label: "Create Strategy",
    route: "/sherloc-media-planner",
    icon: "target",
    accent: "#c763ff",
  },
  {
    label: "Upcoming Campaigns",
    route: "/sherloc-media-campaigns",
    icon: "calendar",
    accent: "#7d7bff",
  },
  {
    label: "Authorizations",
    route: "/authorize",
    icon: "shield",
    accent: "#39e0d6",
  },
  {
    label: "Upload Creative",
    route: "/creative-studio",
    icon: "cloudUpload",
    accent: "#46b9ff",
  },
  {
    label: "Create Report",
    route: "/sherloc-reports",
    icon: "barChart",
    accent: "#b06bff",
  },
  {
    label: "Analyze Performance",
    route: "/optimus",
    icon: "trending",
    accent: "#4ade80",
  },
];

// Inline stroke icons (currentColor) so each card can tint its glyph with the
// Pantheon neon accent without pulling in an icon dependency.
const EDEN_ENTRY_ICONS = {
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.4" />
      <line x1="12" y1="1.6" x2="12" y2="5.2" />
      <line x1="12" y1="18.8" x2="12" y2="22.4" />
      <line x1="1.6" y1="12" x2="5.2" y2="12" />
      <line x1="18.8" y1="12" x2="22.4" y2="12" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="4.8" width="17" height="15.7" rx="2.4" />
      <line x1="3.5" y1="9.4" x2="20.5" y2="9.4" />
      <line x1="8" y1="2.6" x2="8" y2="6.4" />
      <line x1="16" y1="2.6" x2="16" y2="6.4" />
      <line x1="7.4" y1="13.2" x2="7.5" y2="13.2" />
      <line x1="12" y1="13.2" x2="12.1" y2="13.2" />
      <line x1="16.6" y1="13.2" x2="16.7" y2="13.2" />
      <line x1="7.4" y1="16.8" x2="7.5" y2="16.8" />
      <line x1="12" y1="16.8" x2="12.1" y2="16.8" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2.6l7.4 3.1v5.2c0 4.8-3.1 8.4-7.4 10-4.3-1.6-7.4-5.2-7.4-10V5.7L12 2.6z" />
      <rect x="9.3" y="11.4" width="5.4" height="4.4" rx="1" />
      <path d="M10.3 11.4v-1.1a1.7 1.7 0 0 1 3.4 0v1.1" />
    </>
  ),
  barChart: (
    <>
      <line x1="4.4" y1="20" x2="20.4" y2="20" />
      <rect x="6" y="12.5" width="3.1" height="6.2" rx="0.8" />
      <rect x="10.9" y="8.5" width="3.1" height="10.2" rx="0.8" />
      <rect x="15.8" y="5" width="3.1" height="13.7" rx="0.8" />
    </>
  ),
  trending: (
    <>
      <polyline points="3.5,16.5 9,11 13,15 20.5,7.5" />
      <polyline points="15.5,7.5 20.5,7.5 20.5,12.5" />
    </>
  ),
  cloudUpload: (
    <>
      <path d="M7.2 18.5a4.3 4.3 0 0 1-.6-8.55 5.4 5.4 0 0 1 10.5-1.2 3.9 3.9 0 0 1 .5 7.75" />
      <polyline points="9.5,13.4 12,10.9 14.5,13.4" />
      <line x1="12" y1="10.9" x2="12" y2="19.4" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5.5h16a1.8 1.8 0 0 1 1.8 1.8v7.4a1.8 1.8 0 0 1-1.8 1.8H9.6L5 21v-3.5H4a1.8 1.8 0 0 1-1.8-1.8V7.3A1.8 1.8 0 0 1 4 5.5z" />
      <line x1="7.2" y1="11" x2="7.3" y2="11" />
      <line x1="12" y1="11" x2="12.1" y2="11" />
      <line x1="16.8" y1="11" x2="16.9" y2="11" />
    </>
  ),
  chevron: <polyline points="9,5.5 15.5,12 9,18.5" />,
  sparkle: (
    <path
      d="M12 2.5l1.9 5.8a3 3 0 0 0 1.9 1.9L21.5 12l-5.8 1.9a3 3 0 0 0-1.9 1.9L12 21.5l-1.9-5.8a3 3 0 0 0-1.9-1.9L2.5 12l5.8-1.9a3 3 0 0 0 1.9-1.9z"
      fill="currentColor"
      stroke="none"
    />
  ),
  send: (
    <path d="M12 3.2l7.4 16.2a0.7 0.7 0 0 1-0.95 0.9L12 17.4l-6.45 2.9a0.7 0.7 0 0 1-0.95-0.9z" fill="currentColor" stroke="none" />
  ),
};

function EdenEntryIcon({ name, className }) {
  const glyph = EDEN_ENTRY_ICONS[name];
  if (!glyph) return null;
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {glyph}
    </svg>
  );
}

export function EdenEntryOverlay({
  visible = true,
  displayName = "",
  onDismiss,
  onSubmit = dispatchEdenEntryPrompt,
}) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [isPromptInvalid, setIsPromptInvalid] = useState(false);
  const promptRef = useRef(null);
  const scrimRef = useRef(null);
  // The box grows with what is typed (up to its CSS max-height), so a
  // multi-line request reads whole instead of scrolling under its top edge.
  useLayoutEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = "auto";
    if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`;
  }, [prompt]);
  // Where the current press began. Defaults to "on the scrim" so a plain
  // backdrop click still dismisses in environments that never emit pointerdown.
  const pressBeganOnScrimRef = useRef(true);
  const trimmedDisplayName = String(displayName || "").trim();
  const headline = trimmedDisplayName
    ? `What would you like to accomplish, ${trimmedDisplayName}?`
    : "What would you like to accomplish?";

  const dismiss = useCallback(() => {
    setEdenEntryDismissedForSession(true);
    onDismiss?.();
  }, [onDismiss]);

  const submitPrompt = useCallback(
    (rawPrompt) => {
      const cleanPrompt = String(rawPrompt || "").trim();
      if (!cleanPrompt) {
        setIsPromptInvalid(true);
        promptRef.current?.focus?.();
        return false;
      }

      const submitted = onSubmit(cleanPrompt);
      if (submitted === false) return false;
      setIsPromptInvalid(false);
      setPrompt("");
      dismiss();
      return true;
    },
    [dismiss, onSubmit]
  );

  const handleQuickAction = useCallback(
    (action) => {
      if (!action?.route) return;
      dismiss();
      navigate(action.route);
    },
    [dismiss, navigate]
  );

  // Capture phase so a press is latched even if a control inside the dialog
  // stops propagation on pointerdown.
  const handleOverlayPointerDownCapture = useCallback((event) => {
    pressBeganOnScrimRef.current =
      event.target === scrimRef.current || event.target === event.currentTarget;
  }, []);

  const handleScrimClick = useCallback(
    (event) => {
      // The scrim or exposed black page edge is the dismissal boundary. Keep dismissal
      // intentional: reject anything that bubbled out of the dialog, and reject
      // a release from a press that began inside it (text selection dragged
      // past the panel edge, a mis-drag off a control).
      if (event.target !== event.currentTarget) return;
      if (!pressBeganOnScrimRef.current) return;
      pressBeganOnScrimRef.current = true;
      dismiss();
    },
    [dismiss]
  );

  useEffect(() => {
    if (!visible) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") dismiss();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [dismiss, visible]);

  if (!visible) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    submitPrompt(prompt);
  };

  const handlePromptKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    submitPrompt(prompt);
  };

  return (
    <section
      className="eden-entry-overlay"
      aria-label="Eden Entry"
      onPointerDownCapture={handleOverlayPointerDownCapture}
      onClick={handleScrimClick}
    >
      {/* Both the scrim and the exposed edge after scrolling dismiss on a
          direct click. The shared handler rejects clicks from inside the form
          and releases from a press that began on a control. */}
      <div
        ref={scrimRef}
        className="eden-entry-scrim"
        aria-hidden="true"
        onClick={handleScrimClick}
      />
      <form
        className="eden-entry-card"
        role="dialog"
        aria-modal="false"
        aria-labelledby="eden-entry-title"
        onSubmit={handleSubmit}
      >
        <button
          className="eden-entry-cortex-orb"
          type="button"
          onClick={dismiss}
          aria-label="Open Cortex"
          title="Open Cortex"
        >
          <span className="eden-entry-cortex-orb-label" aria-hidden="true">
            Cortex
          </span>
        </button>
        <div className="eden-entry-identity">
          <span className="eden-entry-avatar-shell" aria-hidden="true">
            <img src={profileSettingsImage} alt="" className="eden-entry-avatar" draggable={false} />
          </span>
          <h2 id="eden-entry-title">
            {trimmedDisplayName ? (
              <>
                What would you like to accomplish,{" "}
                <span className="eden-entry-headline-accent">{trimmedDisplayName}</span>?
              </>
            ) : (
              headline
            )}
          </h2>
        </div>

        <label className="eden-entry-sr-only" htmlFor="eden-entry-prompt">
          Ask Eden
        </label>
        <div
          className={`eden-entry-composer${
            prompt.trim() ? " eden-entry-composer--has-text" : ""
          }${isPromptInvalid ? " is-invalid" : ""}`}
        >
          <div className="eden-entry-input-row">
            <EdenEntryIcon name="chat" className="eden-entry-input-icon" />
            <textarea
              ref={promptRef}
              id="eden-entry-prompt"
              className="eden-entry-textarea"
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
                if (isPromptInvalid) setIsPromptInvalid(false);
              }}
              onKeyDown={handlePromptKeyDown}
              placeholder="Ask Eden..."
              rows={2}
              aria-invalid={isPromptInvalid}
              autoFocus
            />
          </div>
          <EdenEntryAttachmentBoundary />
          <button
            className="eden-entry-send cphere-primary-action"
            type="submit"
            aria-label="Send message to Eden"
            disabled={!prompt.trim()}
          >
            <EdenEntryIcon name="send" className="eden-entry-send-icon" />
            <span className="eden-entry-send-label">ASK</span>
          </button>
        </div>

        <div className="eden-entry-suggestions" aria-label="Suggested Eden prompts">
          {EDEN_ENTRY_SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion.label}
              type="button"
              className="eden-entry-suggestion"
              onClick={() => handleQuickAction(suggestion)}
            >
              <span
                className="eden-entry-suggestion-icon"
                style={{ color: suggestion.accent }}
                aria-hidden="true"
              >
                <EdenEntryIcon name={suggestion.icon} />
              </span>
              <span className="eden-entry-suggestion-body">
                <span className="eden-entry-suggestion-title">{suggestion.label}</span>
              </span>
              <EdenEntryIcon name="chevron" className="eden-entry-suggestion-chevron" />
            </button>
          ))}
        </div>
      </form>
    </section>
  );
}

export function EdenEntryAttachmentBoundary({
  policy = EDEN_ENTRY_ATTACHMENT_POLICY,
} = {}) {
  if (!policy?.enabled) return null;

  // TODO: Enable only when Eden Chat or Creative Studio exposes an advertiser-scoped
  // attachment handoff that confirms campaign association before any upload.
  return null;
}
